import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Lock, Users, Shield, Stethoscope, Tractor, Calculator, Cpu, Award,
  Plus, ArrowRight, ArrowLeft, ChevronRight, MessageSquare, Trash2,
  FileText, Target, Check, DollarSign, MapPin, Briefcase, Clock,
  Edit3, X, Send, LogIn, Activity, LayoutDashboard, AlertTriangle,
  Car, Leaf, TrendingUp, BarChart3, Monitor, Paperclip, Download, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import CrossbaseDemo from './CrossbaseDemo';
import CrossbaseFinanceDemo from './CrossbaseFinanceDemo';
import { PartnershipsView } from './PartnershipsView';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VOXIT_LOGO = 'https://customer-assets.emergentagent.com/job_8fab86eb-85cd-46bf-b407-6c1fe82b0eb3/artifacts/wcvtgo52_1.png';
const SME_LOGO = 'https://customer-assets.emergentagent.com/job_8fab86eb-85cd-46bf-b407-6c1fe82b0eb3/artifacts/z4hsx05x_image.png';

const C = { bg: '#f8fafc', card: '#ffffff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b', light: '#94a3b8', blue: '#2563eb', blueBg: '#eff6ff', green: '#059669', greenBg: '#ecfdf5', purple: '#7c3aed', purpleBg: '#f5f3ff', amber: '#d97706', amberBg: '#fffbeb', red: '#dc2626', teal: '#0891b2' };

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'initiatives', label: 'Initiatives', icon: Briefcase },
  { id: 'requirements', label: 'Requirements', icon: FileText },
  { id: 'kanban', label: 'Kanban Board', icon: Target },
  { id: 'meetings', label: 'Meetings', icon: Users },
  { id: 'partnerships', label: 'Partnerships', icon: Briefcase },
  { id: 'demo', label: 'Demo', icon: Monitor, allRoles: true },
  { id: 'revenue', label: 'Revenue', icon: DollarSign },
  { id: 'activity', label: 'Activity Log', icon: Activity, voxitOnly: true },
];

const STATUS_COLS = [
  { key: 'todo', label: 'To Do', color: C.muted, bg: '#f1f5f9' },
  { key: 'in_progress', label: 'In Progress', color: C.blue, bg: C.blueBg },
  { key: 'done', label: 'Done', color: C.green, bg: C.greenBg },
];

/* ═══════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════ */
const api = (path, token, opts = {}) =>
  fetch(`${API_URL}/api/sme-workspace${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(async (r) => {
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Request failed');
    return r.json();
  });

const RoleBadge = ({ role }) => (
  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: role === 'voxit' ? C.blueBg : C.greenBg, color: role === 'voxit' ? C.blue : C.green }}>
    {role === 'voxit' ? 'VoxIT' : 'Ministry'}
  </span>
);

const ApprovedBadge = ({ item }) => {
  const approvals = item?.approvals || {};
  const voxitApproved = !!approvals.voxit;
  const ministryApproved = !!approvals.ministry;
  const fullyApproved = voxitApproved && ministryApproved;
  if (!voxitApproved && !ministryApproved) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: fullyApproved ? '#dcfce7' : '#fef9c3', color: fullyApproved ? '#15803d' : '#a16207' }}>
      {fullyApproved ? <><Check size={10} /> Handshake</> : voxitApproved ? 'VoxIT approved' : 'Ministry approved'}
    </span>
  );
};

const ApprovalStatus = ({ item, token, type, id, onRefresh, user }) => {
  const approvals = item?.approvals || {};
  const voxitApproved = !!approvals.voxit;
  const ministryApproved = !!approvals.ministry;
  const myRole = user?.role;
  const iApproved = !!approvals[myRole];

  const doApprove = async () => {
    try { await api(`/${type}/${id}/approve`, token, { method: 'POST' }); onRefresh(); toast.success('Approved'); }
    catch { toast.error('Failed'); }
  };
  const doUnapprove = async () => {
    try { await api(`/${type}/${id}/unapprove`, token, { method: 'POST' }); onRefresh(); toast.success('Approval withdrawn'); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1">
        <span className={`w-4 h-4 rounded-full flex items-center justify-center ${voxitApproved ? 'text-white' : ''}`} style={{ background: voxitApproved ? C.blue : '#e2e8f0', border: voxitApproved ? 'none' : `1px solid ${C.border}` }}>
          {voxitApproved && <Check size={10} />}
        </span>
        <span className="text-[10px] font-medium" style={{ color: voxitApproved ? C.blue : C.light }}>VoxIT</span>
      </div>
      <div className="flex items-center gap-1">
        <span className={`w-4 h-4 rounded-full flex items-center justify-center ${ministryApproved ? 'text-white' : ''}`} style={{ background: ministryApproved ? C.green : '#e2e8f0', border: ministryApproved ? 'none' : `1px solid ${C.border}` }}>
          {ministryApproved && <Check size={10} />}
        </span>
        <span className="text-[10px] font-medium" style={{ color: ministryApproved ? C.green : C.light }}>Ministry</span>
      </div>
      {!iApproved ? (
        <button onClick={doApprove} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold text-white transition-all hover:brightness-110" style={{ background: '#16a34a' }}>
          <Check size={9} /> Approve
        </button>
      ) : (
        <button onClick={doUnapprove} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold transition-all hover:brightness-95" style={{ background: '#fef2f2', color: C.red }}>
          <X size={9} /> Withdraw
        </button>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
const SMEWorkspacePage = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [tab, setTab] = useState('dashboard');
  const [tasks, setTasks] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState({ revenue_visible: false });
  const [modal, setModal] = useState(null);

  const load = useCallback(async (t) => {
    try {
      const [ts, ini, req, mtg, prj, sett] = await Promise.all([
        api('/tasks', t), api('/initiatives', t), api('/requirements', t), api('/meetings', t), api('/projects', t), api('/settings', t),
      ]);
      setTasks(ts); setInitiatives(ini); setRequirements(req); setMeetings(mtg); setProjects(prj); setSettings(sett);
    } catch { /* ignore */ }
  }, []);

  const loadActivity = useCallback(async (t) => {
    try { setActivityLog(await api('/activity-log', t)); } catch { /* ignore */ }
  }, []);

  const loadScenarios = useCallback(async (t) => {
    try { setScenarios(await api('/revenue-scenarios', t)); } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);
  useEffect(() => { if (token && user?.role === 'voxit' && tab === 'activity') loadActivity(token); }, [token, user, tab, loadActivity]);
  useEffect(() => { if (token && tab === 'revenue') loadScenarios(token); }, [token, tab, loadScenarios]);

  /* ── Login required ── */
  const handleLogin = (userData, tokenData) => {
    setUser(userData);
    setToken(tokenData);
    if (userData.role === 'ict') setTab('demo');
    if (userData.role === 'hfz') setTab('partnerships');
  };

  if (!user || !token) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  const visibleTabs = TABS.filter(t => {
    if (user.role === 'ict') return t.id === 'demo';
    if (user.role === 'hfz') return t.id === 'partnerships';
    if (t.voxitOnly && user.role !== 'voxit') return false;
    if (t.id === 'revenue' && !settings.revenue_visible && user.role !== 'voxit') return false;
    return true;
  });

  const toggleRevenue = async () => {
    const newVal = !settings.revenue_visible;
    try {
      await api('/settings', token, { method: 'PUT', body: JSON.stringify({ revenue_visible: newVal }) });
      setSettings(s => ({ ...s, revenue_visible: newVal }));
      toast.success(newVal ? 'Revenue tab visible to Ministry' : 'Revenue tab hidden from Ministry');
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="sme-workspace" className="min-h-screen flex flex-col" style={{ background: C.bg }}>
      {/* Top Bar */}
      <div className="sticky top-0 z-50 px-5 py-3 flex items-center justify-between" style={{ background: '#0f172a', borderBottom: '2px solid #1e293b' }}>
        <div className="flex items-center gap-3">
          <img src={VOXIT_LOGO} alt="VoxIT" className="h-6 object-contain" />
          <span className="text-xs" style={{ color: '#475569' }}>x</span>
          <img src={SME_LOGO} alt="Ministry of SME" className="h-7 object-contain" />
          <span className="hidden sm:block w-px h-5 mx-1" style={{ background: '#334155' }} />
          <span className="text-sm font-bold hidden sm:block tracking-wide" style={{ color: '#f1f5f9' }}>Collaboration Workspace</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium px-2.5 py-1 rounded-md" style={{ background: '#1e293b', color: '#94a3b8' }}>{user.name}</span>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="sticky top-[52px] z-40 px-4 py-2 flex items-center gap-1.5 overflow-x-auto" style={{ background: '#1e293b', borderBottom: '2px solid #2563eb30' }}>
        {visibleTabs.map(t => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
            style={{
              background: tab === t.id ? C.blue : 'transparent',
              color: tab === t.id ? '#ffffff' : '#94a3b8',
              border: tab === t.id ? 'none' : '1px solid transparent',
              boxShadow: tab === t.id ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
            }}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
        {user.role === 'voxit' && (
          <label className="flex items-center gap-1.5 ml-auto cursor-pointer whitespace-nowrap pl-4">
            <input type="checkbox" checked={settings.revenue_visible} onChange={toggleRevenue} className="w-3.5 h-3.5 rounded cursor-pointer accent-blue-500" />
            <span className="text-[11px] font-medium" style={{ color: '#94a3b8' }}>Revenue visible</span>
          </label>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {tab === 'dashboard' && <DashboardView tasks={tasks} initiatives={initiatives} meetings={meetings} requirements={requirements} projects={projects} token={token} user={user} onRefresh={() => load(token)} setModal={setModal} />}
        {tab === 'kanban' && <KanbanBoard tasks={tasks} token={token} user={user} onRefresh={() => load(token)} setModal={setModal} projects={projects} />}
        {tab === 'initiatives' && <InitiativesView items={initiatives} token={token} user={user} onRefresh={() => load(token)} setModal={setModal} projects={projects} />}
        {tab === 'requirements' && <RequirementsView items={requirements} token={token} user={user} onRefresh={() => load(token)} setModal={setModal} />}
        {tab === 'meetings' && <MeetingsView items={meetings} token={token} user={user} onRefresh={() => load(token)} setModal={setModal} />}
        {tab === 'demo' && <DemoSelector />}
        {tab === 'partnerships' && <PartnershipsView token={token} user={user} toast={toast} />}
        {tab === 'revenue' && <RevenueCalculator token={token} user={user} scenarios={scenarios} onRefresh={() => loadScenarios(token)} setModal={setModal} />}
        {tab === 'activity' && <ActivityLogView items={activityLog} />}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {modal && <ModalOverlay onClose={() => setModal(null)}>{modal}</ModalOverlay>}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════
   LOGIN SCREEN
   ═══════════════════════════════════════════ */
const LoginScreen = ({ onLogin }) => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await api('/login', '', { method: 'POST', body: JSON.stringify({ name, password }), headers: {} });
      onLogin({ name: data.name, role: data.role }, data.token);
      toast.success(`Welcome, ${data.name}`);
    } catch (err) { toast.error(err.message || 'Login failed'); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="workspace-login" className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #f8fafc 50%, #ecfdf5 100%)' }}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md mx-4 p-10 rounded-2xl bg-white text-center" style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.08)', border: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-center gap-4 mb-5">
          <img src={VOXIT_LOGO} alt="VoxIT" className="h-9 object-contain" />
          <span className="text-lg font-light" style={{ color: C.light }}>x</span>
          <img src={SME_LOGO} alt="Ministry" className="h-11 object-contain" />
        </div>
        <h1 className="text-xl font-bold mb-1" style={{ color: C.text }}>Collaboration Workspace</h1>
        <p className="text-sm mb-6" style={{ color: C.muted }}>Sign in to access the project tracker</p>
        <form onSubmit={handleSubmit} className="space-y-3 text-left">
          <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: C.muted }}>Your Name</label>
            <input data-testid="ws-name-input" value={name} onChange={e => setName(e.target.value)} required placeholder="Enter your name"
              className="w-full px-3.5 py-2.5 rounded-xl outline-none text-sm" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }} />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: C.muted }}>Password</label>
            <input data-testid="ws-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Enter workspace password"
              className="w-full px-3.5 py-2.5 rounded-xl outline-none text-sm" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }} />
          </div>
          <button data-testid="ws-login-btn" type="submit" disabled={loading}
            className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.blue }}>
            {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><LogIn size={16} /> Enter Workspace</>}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   DASHBOARD VIEW
   ═══════════════════════════════════════════ */
const ICON_MAP = { stethoscope: Stethoscope, calculator: Calculator, shield: Shield, leaf: Leaf, car: Car, briefcase: Briefcase, target: Target, cpu: Cpu, award: Award, tractor: Tractor, users: Users };

const getIcon = (name) => ICON_MAP[name] || Briefcase;

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
  return diff;
};

const DeadlineBadge = ({ date, compact }) => {
  if (!date) return null;
  const days = daysUntil(date);
  const isPast = days < 0;
  const isUrgent = days >= 0 && days <= 7;
  const isSoon = days > 7 && days <= 30;
  const color = isPast ? C.red : isUrgent ? '#b45309' : isSoon ? C.amber : C.green;
  const bg = isPast ? '#fef2f2' : isUrgent ? '#fef3c7' : isSoon ? C.amberBg : C.greenBg;
  const label = isPast ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days}d left`;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${compact ? '' : 'ml-1'}`} style={{ background: bg, color }}>
      {label}
    </span>
  );
};

const calcProjectProgress = (projectTasks) => {
  if (projectTasks.length === 0) return 0;
  const score = projectTasks.reduce((sum, t) => {
    if (t.status === 'done') return sum + 100;
    if (t.status === 'in_progress') return sum + 50;
    return sum;
  }, 0);
  return Math.round(score / projectTasks.length);
};

const DashboardView = ({ tasks, initiatives, meetings, requirements, projects, token, user, onRefresh, setModal }) => {
  const todoCount = tasks.filter(t => t.status === 'todo').length;
  const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
  const doneCount = tasks.filter(t => t.status === 'done').length;
  const totalTasks = tasks.length;
  const overallProgress = totalTasks > 0 ? Math.round(((doneCount + inProgressCount * 0.5) / totalTasks) * 100) : 0;

  // Group tasks by project
  const tasksByProject = {};
  projects.forEach(p => { tasksByProject[p.key] = []; });
  tasks.forEach(t => {
    const p = t.project || 'general';
    if (tasksByProject[p]) tasksByProject[p].push(t);
  });

  // Map initiatives to projects
  const initiativesByProject = {};
  projects.forEach(p => { initiativesByProject[p.key] = []; });
  initiatives.forEach(ini => {
    const p = ini.project || 'general';
    if (initiativesByProject[p]) initiativesByProject[p].push(ini);
  });

  // Build dynamic project data
  const projectCards = projects.map(p => {
    const projectTasks = tasksByProject[p.key] || [];
    const projectInitiatives = initiativesByProject[p.key] || [];
    const progress = calcProjectProgress(projectTasks);
    const mainInitiative = projectInitiatives[0];
    const status = mainInitiative?.status || (progress === 100 ? 'Completed' : progress > 0 ? 'In Progress' : 'Planning');
    const description = p.description || mainInitiative?.description || '';
    const needsLicense = projectInitiatives.some(i => /blocked|zra|license|zicta/i.test(i.status));
    const licenseDetail = projectInitiatives.find(i => /blocked|zra|license|zicta/i.test(i.status));
    const IconComp = getIcon(p.icon);
    return { ...p, progress, status, description, needsLicense, licenseDetail, taskCount: projectTasks.length, IconComp };
  });

  const licenseProjects = projectCards.filter(p => p.needsLicense);

  const openAddProject = () => setModal(<AddProjectForm token={token} onDone={() => { setModal(null); onRefresh(); }} />);
  const openEditProject = (p) => setModal(<EditProjectForm item={p} token={token} onDone={() => { setModal(null); onRefresh(); }} />);

  const deleteProject = async (id) => {
    try { await api(`/projects/${id}`, token, { method: 'DELETE' }); onRefresh(); toast.success('Project deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="dashboard-view">
      {/* Header Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Tasks', value: totalTasks, icon: Target, color: C.blue, bg: C.blueBg },
          { label: 'In Progress', value: inProgressCount, icon: TrendingUp, color: C.amber, bg: C.amberBg },
          { label: 'Completed', value: doneCount, icon: Check, color: C.green, bg: C.greenBg },
          { label: 'Initiatives', value: initiatives.length, icon: Briefcase, color: C.purple, bg: C.purpleBg },
        ].map(s => (
          <div key={s.label} className="p-4 rounded-xl bg-white" style={{ border: `1px solid ${C.border}` }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: s.bg }}>
                <s.icon size={16} style={{ color: s.color }} />
              </div>
            </div>
            <div className="text-xl font-bold" style={{ color: C.text }}>{s.value}</div>
            <div className="text-xs font-medium" style={{ color: C.muted }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Overall Progress */}
      <div className="p-4 rounded-xl bg-white mb-6" style={{ border: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold" style={{ color: C.text }}>Overall Partnership Progress</span>
          <span className="text-sm font-bold" style={{ color: C.blue }}>{overallProgress}%</span>
        </div>
        <div className="h-2.5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${overallProgress}%`, background: `linear-gradient(90deg, ${C.blue}, ${C.green})` }} />
        </div>
        <div className="flex items-center gap-4 mt-2">
          <span className="text-xs" style={{ color: C.muted }}>{todoCount} To Do</span>
          <span className="text-xs" style={{ color: C.amber }}>{inProgressCount} In Progress</span>
          <span className="text-xs" style={{ color: C.green }}>{doneCount} Done</span>
        </div>
      </div>

      {/* Financial License Help Needed */}
      {licenseProjects.length > 0 && (
        <div data-testid="license-help-section" className="p-4 rounded-xl mb-6" style={{ background: '#fef3c7', border: '1px solid #fbbf24' }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} style={{ color: '#b45309' }} />
            <span className="text-sm font-bold" style={{ color: '#92400e' }}>Financial License Help Needed</span>
          </div>
          {licenseProjects.map(p => (
            <div key={p.key} className="flex items-start gap-3 p-3 rounded-lg bg-white/70 mb-2 last:mb-0">
              <p.IconComp size={16} style={{ color: p.color }} className="flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-sm font-semibold" style={{ color: C.text }}>{p.name}</span>
                <p className="text-xs mt-0.5" style={{ color: '#92400e' }}>
                  {p.licenseDetail?.description || p.licenseDetail?.status || 'Requires licensing or API access.'}
                </p>
                {p.licenseDetail?.ministry_action && (
                  <p className="text-xs mt-1 font-medium" style={{ color: '#92400e' }}>Ministry action: {p.licenseDetail.ministry_action}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Project Cards */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: C.text }}>Project Overview</h3>
        <button data-testid="add-project-btn" onClick={openAddProject} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-white transition-all hover:brightness-110" style={{ background: C.blue }}>
          <Plus size={15} /> Add Project
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {projectCards.map(p => (
          <div key={p.id} data-testid={`project-card-${p.key}`} className="p-5 rounded-xl bg-white" style={{ border: p.approved ? '2px solid #22c55e' : `1px solid ${C.border}`, borderTop: `3px solid ${p.color}` }}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${p.color}15` }}>
                  <p.IconComp size={18} style={{ color: p.color }} />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold" style={{ color: C.text }}>{p.name}</span>
                    <ApprovedBadge item={p} />
                  </div>
                  <div className="text-xs" style={{ color: C.muted }}>{p.subtitle}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <RoleBadge role={p.owner || 'voxit'} />
                <button onClick={() => openEditProject(p)} className="p-1 rounded hover:bg-slate-100"><Edit3 size={13} style={{ color: C.muted }} /></button>
                <button onClick={() => deleteProject(p.id)} className="p-1 rounded hover:bg-red-50"><Trash2 size={13} style={{ color: C.red }} /></button>
              </div>
            </div>
            <p className="text-xs leading-relaxed mb-2" style={{ color: C.muted }}>{p.description}</p>
            {(p.developers > 0 && p.hourly_rate > 0 && p.duration_months > 0) && (() => {
              const raw = p.developers * p.hourly_rate * 165 * p.duration_months;
              const oh = raw * ((p.overhead_percent ?? 15) / 100);
              return (
              <div className="p-2 rounded-lg mb-2" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium" style={{ color: '#0369a1' }}>{p.developers} dev x ${p.hourly_rate}/hr x 165h x {p.duration_months}mo</span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-[10px]" style={{ color: C.muted }}>Raw: ${raw.toLocaleString()} + {p.overhead_percent ?? 15}% overhead</span>
                  <span className="text-xs font-bold" style={{ color: '#0369a1' }}>${(raw + oh).toLocaleString()}</span>
                </div>
                {p.funding_type && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded mt-1 inline-block" style={{ background: p.funding_type === 'grant' ? C.greenBg : p.funding_type === 'paid' ? C.amberBg : C.purpleBg, color: p.funding_type === 'grant' ? C.green : p.funding_type === 'paid' ? C.amber : C.purple }}>{p.funding_type}</span>}
              </div>);
            })()}
            <div className="mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium" style={{ color: C.muted }}>Progress ({p.taskCount} tasks)</span>
                <span className="text-xs font-bold" style={{ color: p.color }}>{p.progress}%</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${p.progress}%`, background: p.color }} />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: p.needsLicense ? C.amberBg : p.progress >= 80 ? C.greenBg : C.blueBg, color: p.needsLicense ? C.amber : p.progress >= 80 ? C.green : C.blue }}>
                  {p.status}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {p.launch_date && (
                  <>
                    <span className="text-xs" style={{ color: C.light }}>{new Date(p.launch_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                    <DeadlineBadge date={p.launch_date} compact />
                  </>
                )}
              </div>
            </div>
            <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
              <ApprovalStatus item={p} token={token} type="projects" id={p.id} onRefresh={onRefresh} user={user} />
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-4 rounded-xl bg-white" style={{ border: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} style={{ color: C.amber }} />
            <span className="text-sm font-bold" style={{ color: C.text }}>Recent Meetings</span>
          </div>
          {meetings.slice(0, 3).map(m => (
            <div key={m.id} className="p-3 rounded-lg mb-2 last:mb-0" style={{ background: C.bg }}>
              <div className="text-xs font-semibold" style={{ color: C.text }}>{m.title}</div>
              <div className="text-xs" style={{ color: C.light }}>{m.date}</div>
            </div>
          ))}
          {meetings.length === 0 && <div className="text-xs text-center py-4" style={{ color: C.light }}>No meetings yet</div>}
        </div>
        <div className="p-4 rounded-xl bg-white" style={{ border: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-2 mb-3">
            <FileText size={16} style={{ color: C.purple }} />
            <span className="text-sm font-bold" style={{ color: C.text }}>Recent Requirements</span>
          </div>
          {requirements.slice(0, 3).map(r => (
            <div key={r.id} className="p-3 rounded-lg mb-2 last:mb-0" style={{ background: C.bg }}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold" style={{ color: C.text }}>{r.title}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: r.priority === 'high' ? '#fef2f2' : C.amberBg, color: r.priority === 'high' ? C.red : C.amber }}>{r.priority}</span>
              </div>
              <div className="text-xs" style={{ color: C.light }}>{r.project}</div>
            </div>
          ))}
          {requirements.length === 0 && <div className="text-xs text-center py-4" style={{ color: C.light }}>No requirements yet</div>}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   KANBAN BOARD
   ═══════════════════════════════════════════ */
const KanbanBoard = ({ tasks, token, user, onRefresh, setModal, projects }) => {

  const getProjectLaunch = (projKey) => {
    const p = projects?.find(pr => pr.key === projKey);
    return p?.launch_date || null;
  };

  const moveTask = async (taskId, newStatus) => {
    try {
      await api(`/tasks/${taskId}`, token, { method: 'PUT', body: JSON.stringify({ status: newStatus }) });
      onRefresh();
    } catch { toast.error('Failed to move task'); }
  };

  const deleteTask = async (taskId) => {
    try { await api(`/tasks/${taskId}`, token, { method: 'DELETE' }); onRefresh(); toast.success('Task deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  const openAddTask = () => setModal(
    <AddTaskForm token={token} user={user} onDone={() => { setModal(null); onRefresh(); }} />
  );

  const openComment = (task) => setModal(
    <AddCommentForm task={task} token={token} user={user} onDone={() => { setModal(null); onRefresh(); }} />
  );

  const openEdit = (task) => setModal(
    <EditTaskForm task={task} token={token} onDone={() => { setModal(null); onRefresh(); }} />
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold" style={{ color: C.text }}>Kanban Board</h2>
        <button data-testid="add-task-btn" onClick={openAddTask} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-white transition-all hover:brightness-110" style={{ background: C.blue }}>
          <Plus size={15} /> Add Task
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {STATUS_COLS.map(col => {
          const colTasks = tasks.filter(t => t.status === col.key);
          return (
            <div key={col.key} className="rounded-xl p-3" style={{ background: col.bg, border: `1px solid ${C.border}` }}>
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-sm font-bold" style={{ color: col.color }}>{col.label}</span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-white" style={{ color: col.color }}>{colTasks.length}</span>
              </div>
              <div className="space-y-2.5">
                {colTasks.map(task => (
                  <div key={task.id} data-testid={`task-card-${task.id}`} className="p-3.5 rounded-lg bg-white" style={{ border: `1px solid ${C.border}`, borderLeft: `3px solid ${task.assigned_to === 'voxit' ? C.blue : C.green}` }}>
                    <div className="flex items-start justify-between mb-1.5">
                      <span className="text-sm font-semibold leading-tight" style={{ color: C.text }}>{task.title}</span>
                      <div className="flex gap-1 ml-2 flex-shrink-0">
                        <button onClick={() => openEdit(task)} className="p-1 rounded hover:bg-slate-100"><Edit3 size={13} style={{ color: C.muted }} /></button>
                        <button onClick={() => deleteTask(task.id)} className="p-1 rounded hover:bg-red-50"><Trash2 size={13} style={{ color: C.red }} /></button>
                      </div>
                    </div>
                    {task.description && <p className="text-xs mb-2 leading-relaxed" style={{ color: C.muted }}>{task.description}</p>}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <RoleBadge role={task.assigned_to} />
                        {task.project && task.project !== 'general' && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: C.blueBg, color: C.blue }}>
                            {task.project}
                          </span>
                        )}
                        {task.deadline && <DeadlineBadge date={task.deadline} compact />}
                        {!task.deadline && task.project && getProjectLaunch(task.project) && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: C.bg, color: C.light }}>
                            Proj: {new Date(getProjectLaunch(task.project)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {task.comments?.length > 0 && (
                          <span className="text-xs flex items-center gap-0.5" style={{ color: C.muted }}>
                            <MessageSquare size={11} /> {task.comments.length}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Move + Comment buttons */}
                    <div className="flex items-center gap-1.5 mt-2.5 pt-2.5" style={{ borderTop: `1px solid ${C.border}` }}>
                      {col.key !== 'todo' && (
                        <button onClick={() => moveTask(task.id, col.key === 'done' ? 'in_progress' : 'todo')}
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium transition-all hover:brightness-95" style={{ background: C.bg, color: C.muted }}>
                          <ArrowLeft size={11} /> {col.key === 'done' ? 'In Progress' : 'To Do'}
                        </button>
                      )}
                      {col.key !== 'done' && (
                        <button onClick={() => moveTask(task.id, col.key === 'todo' ? 'in_progress' : 'done')}
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium text-white transition-all hover:brightness-110" style={{ background: col.key === 'todo' ? C.blue : C.green }}>
                          {col.key === 'todo' ? 'In Progress' : 'Done'} <ArrowRight size={11} />
                        </button>
                      )}
                      <button onClick={() => openComment(task)} className="flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium ml-auto transition-all hover:brightness-95" style={{ background: C.bg, color: C.muted }}>
                        <MessageSquare size={11} /> Comment
                      </button>
                    </div>
                    {/* Show comments */}
                    {task.comments?.length > 0 && (
                      <div className="mt-2 pt-2 space-y-1.5" style={{ borderTop: `1px dashed ${C.border}` }}>
                        {task.comments.slice(-2).map(c => (
                          <div key={c.id} className="text-xs p-2 rounded" style={{ background: C.bg }}>
                            <span className="font-semibold" style={{ color: c.author?.role === 'voxit' ? C.blue : C.green }}>{c.author?.name}</span>
                            <span className="ml-1" style={{ color: C.muted }}>{c.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {colTasks.length === 0 && <div className="text-center py-6 text-xs" style={{ color: C.light }}>No tasks</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   INITIATIVES VIEW
   ═══════════════════════════════════════════ */
const InitiativesView = ({ items, token, user, onRefresh, setModal, projects }) => {
  const openAdd = () => setModal(<AddInitiativeForm token={token} onDone={() => { setModal(null); onRefresh(); }} />);
  const openEdit = (ini) => setModal(<EditInitiativeForm item={ini} token={token} onDone={() => { setModal(null); onRefresh(); }} />);

  const deleteIni = async (id) => {
    try { await api(`/initiatives/${id}`, token, { method: 'DELETE' }); onRefresh(); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  const updateDeadline = async (id, newDate) => {
    try { await api(`/initiatives/${id}`, token, { method: 'PUT', body: JSON.stringify({ deadline: newDate }) }); onRefresh(); toast.success('Deadline updated'); }
    catch { toast.error('Failed to update'); }
  };

  const getProjectDeadline = (projKey) => {
    const p = projects?.find(pr => pr.key === projKey);
    return p?.launch_date || null;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold" style={{ color: C.text }}>Joint Initiatives</h2>
        <button data-testid="add-initiative-btn" onClick={openAdd} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-white transition-all hover:brightness-110" style={{ background: C.green }}>
          <Plus size={15} /> Add Initiative
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(ini => {
          const projDeadline = getProjectDeadline(ini.project);
          return (
          <div key={ini.id} className="p-4 rounded-xl bg-white" style={{ border: ini.approved ? '2px solid #22c55e' : `1px solid ${C.border}` }}>
            <div className="flex items-start justify-between mb-2">
              <span className="text-sm font-bold" style={{ color: C.text }}>{ini.title}</span>
              <div className="flex gap-1 ml-2 flex-shrink-0">
                <button onClick={() => openEdit(ini)} className="p-1 rounded hover:bg-slate-100"><Edit3 size={13} style={{ color: C.muted }} /></button>
                <button onClick={() => deleteIni(ini.id)} className="p-1 rounded hover:bg-red-50"><Trash2 size={13} style={{ color: C.red }} /></button>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: C.amberBg, color: C.amber }}>{ini.status}</span>
              <ApprovedBadge item={ini} />
            </div>
            <p className="text-xs leading-relaxed mb-2" style={{ color: C.muted }}>{ini.description}</p>
            <div className="p-2 rounded-lg mb-2" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium" style={{ color: C.text }}>Initiative due</span>
                <div className="flex items-center gap-1">
                  <input type="date" value={ini.deadline || ''} onChange={e => updateDeadline(ini.id, e.target.value)}
                    className="text-[10px] px-1.5 py-0.5 rounded cursor-pointer bg-transparent outline-none" style={{ color: C.text, border: `1px solid ${C.border}` }} />
                  {ini.deadline && <DeadlineBadge date={ini.deadline} compact />}
                </div>
              </div>
              {projDeadline && (
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] font-medium" style={{ color: C.light }}>Project launches</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px]" style={{ color: C.light }}>{new Date(projDeadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <DeadlineBadge date={projDeadline} compact />
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-1.5 pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
              <div className="flex items-start gap-1.5"><span className="text-xs font-bold flex-shrink-0" style={{ color: C.blue }}>VoxIT:</span><span className="text-xs" style={{ color: C.muted }}>{ini.voxit_action}</span></div>
              <div className="flex items-start gap-1.5"><span className="text-xs font-bold flex-shrink-0" style={{ color: C.green }}>Ministry:</span><span className="text-xs" style={{ color: C.muted }}>{ini.ministry_action}</span></div>
            </div>
            <div className="mt-3 pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
              <ApprovalStatus item={ini} token={token} type="initiatives" id={ini.id} onRefresh={onRefresh} user={user} />
            </div>
          </div>
          );
        })}
      </div>
      {items.length === 0 && <EmptyState text="No initiatives yet" />}
    </div>
  );
};

/* ═══════════════════════════════════════════
   REQUIREMENTS VIEW
   ═══════════════════════════════════════════ */
const ALLOWED_UPLOAD_EXTS = ['docx', 'doc', 'pdf', 'xlsx', 'xls', 'png', 'jpg', 'jpeg'];
const ALLOWED_UPLOAD_ACCEPT = '.docx,.doc,.pdf,.xlsx,.xls,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg';
const MAX_UPLOAD_SIZE_BYTES = 25 * 1024 * 1024;

const formatFileSize = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fileIconColor = (ext) => {
  if (['pdf'].includes(ext)) return '#dc2626';
  if (['doc', 'docx'].includes(ext)) return '#2563eb';
  if (['xls', 'xlsx'].includes(ext)) return '#059669';
  if (['png', 'jpg', 'jpeg'].includes(ext)) return '#7c3aed';
  return '#64748b';
};

const RequirementAttachments = ({ req, token, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const attachments = req.attachments || [];

  const handleFiles = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_UPLOAD_EXTS.includes(ext)) {
      toast.error(`File type .${ext} not allowed. Allowed: Word, PDF, Excel, PNG, JPG`);
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      toast.error('File too large (max 25 MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/sme-workspace/requirements/${req.id}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      toast.success(`${file.name} uploaded`);
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (att) => {
    try {
      const res = await fetch(`${API_URL}/api/sme-workspace/requirements/${req.id}/attachments/${att.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      await api(`/requirements/${req.id}/attachments/${att.id}`, token, { method: 'DELETE' });
      toast.success('Attachment deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold flex items-center gap-1.5" style={{ color: C.text }}>
          <Paperclip size={12} /> Attachments {attachments.length > 0 && <span style={{ color: C.muted }}>({attachments.length})</span>}
        </span>
        <button
          data-testid={`upload-attachment-btn-${req.id}`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg font-semibold transition-all hover:brightness-110 disabled:opacity-50"
          style={{ background: C.purpleBg, color: C.purple, border: `1px solid ${C.purple}33` }}
        >
          <Upload size={11} /> {uploading ? 'Uploading...' : 'Upload file'}
        </button>
        <input ref={inputRef} type="file" accept={ALLOWED_UPLOAD_ACCEPT} onChange={handleFiles} className="hidden" data-testid={`file-input-${req.id}`} />
      </div>
      {attachments.length === 0 ? (
        <p className="text-[11px]" style={{ color: C.light }}>No files attached. Word, PDF, Excel, PNG and JPG accepted (max 25 MB).</p>
      ) : (
        <div className="space-y-1.5">
          {attachments.map(att => (
            <div key={att.id} data-testid={`attachment-row-${att.id}`} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
              <span className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'white', border: `1px solid ${C.border}` }}>
                <FileText size={13} style={{ color: fileIconColor(att.ext) }} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate" style={{ color: C.text }}>{att.filename}</div>
                <div className="text-[10px]" style={{ color: C.light }}>
                  {formatFileSize(att.size)} · {att.uploaded_by?.name || 'Unknown'} · {new Date(att.uploaded_at).toLocaleDateString()}
                </div>
              </div>
              <button onClick={() => handleDownload(att)} className="p-1.5 rounded hover:bg-white" data-testid={`download-attachment-${att.id}`} title="Download">
                <Download size={13} style={{ color: C.blue }} />
              </button>
              <button onClick={() => handleDelete(att)} className="p-1.5 rounded hover:bg-red-50" data-testid={`delete-attachment-${att.id}`} title="Delete">
                <Trash2 size={13} style={{ color: C.red }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RequirementsView = ({ items, token, user, onRefresh, setModal }) => {
  const [expanded, setExpanded] = useState(null);
  const openAdd = () => setModal(<AddRequirementForm token={token} onDone={() => { setModal(null); onRefresh(); }} />);
  const openRespond = (req) => setModal(<RespondForm item={req} token={token} onDone={() => { setModal(null); onRefresh(); }} />);
  const openEdit = (req) => setModal(<EditRequirementForm item={req} token={token} onDone={() => { setModal(null); onRefresh(); }} />);

  const deleteReq = async (id) => {
    try { await api(`/requirements/${id}`, token, { method: 'DELETE' }); onRefresh(); toast.success('Requirement deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold" style={{ color: C.text }}>Requirements Portal</h2>
          <p className="text-xs" style={{ color: C.muted }}>{items.length} requirement{items.length !== 1 ? 's' : ''} submitted</p>
        </div>
        <button data-testid="add-requirement-btn" onClick={openAdd} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-white transition-all hover:brightness-110" style={{ background: C.purple }}>
          <Plus size={15} /> Add Requirement
        </button>
      </div>
      <div className="space-y-1.5">
        {items.map(req => {
          const isOpen = expanded === req.id;
          return (
            <div key={req.id} data-testid={`requirement-card-${req.id}`} className="rounded-xl bg-white overflow-hidden transition-all" style={{ border: req.approved ? '2px solid #22c55e' : `1px solid ${C.border}` }}>
              {/* Compact row — always visible */}
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setExpanded(isOpen ? null : req.id)}>
                <ChevronRight size={14} style={{ color: C.muted, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-sm font-semibold truncate" style={{ color: C.text }}>{req.title}</span>
                  {req.attachments?.length > 0 && (
                    <span data-testid={`title-attachment-badge-${req.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: C.blueBg, color: C.blue, border: `1px solid ${C.blue}33` }} title={`${req.attachments.length} file${req.attachments.length > 1 ? 's' : ''} attached`}>
                      <Paperclip size={10} /> {req.attachments.length}
                    </span>
                  )}
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0" style={{ background: req.priority === 'high' ? '#fef2f2' : req.priority === 'medium' ? C.amberBg : C.bg, color: req.priority === 'high' ? C.red : req.priority === 'medium' ? C.amber : C.muted }}>{req.priority}</span>
                <span className="text-[10px] px-2 py-0.5 rounded font-medium flex-shrink-0" style={{ background: C.purpleBg, color: C.purple }}>{req.project}</span>
                {req.responses?.length > 0 && <span className="text-[10px] flex items-center gap-0.5 flex-shrink-0" style={{ color: C.muted }}><MessageSquare size={10} /> {req.responses.length}</span>}
                <ApprovedBadge item={req} />
                <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <button data-testid={`edit-requirement-${req.id}`} onClick={() => openEdit(req)} className="p-1 rounded hover:bg-slate-100"><Edit3 size={13} style={{ color: C.muted }} /></button>
                  <button data-testid={`delete-requirement-${req.id}`} onClick={() => deleteReq(req.id)} className="p-1 rounded hover:bg-red-50"><Trash2 size={13} style={{ color: C.red }} /></button>
                </div>
              </div>
              {/* Expanded detail */}
              {isOpen && (
                <div className="px-4 pb-4 pt-0" style={{ borderTop: `1px solid ${C.border}` }}>
                  <FormattedText text={req.description} className="text-sm leading-relaxed my-3" style={{ color: C.muted }} />
                  <RequirementAttachments req={req} token={token} onRefresh={onRefresh} />
                  {req.responses?.length > 0 && (
                    <div className="mt-3 pt-3 space-y-2" style={{ borderTop: `1px solid ${C.border}` }}>
                      <span className="text-xs font-bold" style={{ color: C.text }}>Responses:</span>
                      {req.responses.map(r => (
                        <div key={r.id} className="p-3 rounded-lg" style={{ background: C.bg }}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold" style={{ color: r.author?.role === 'voxit' ? C.blue : C.green }}>{r.author?.name}</span>
                            <span className="text-xs" style={{ color: C.light }}>{new Date(r.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-xs whitespace-pre-wrap" style={{ color: C.text }}>{r.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={() => openRespond(req)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium transition-all hover:brightness-95" style={{ background: C.bg, color: C.muted, border: `1px solid ${C.border}` }}>
                      <Send size={11} /> Respond
                    </button>
                    <ApprovalStatus item={req} token={token} type="requirements" id={req.id} onRefresh={onRefresh} user={user} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {items.length === 0 && <EmptyState text="No requirements submitted yet" />}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MEETINGS VIEW
   ═══════════════════════════════════════════ */
const MeetingsView = ({ items, token, user, onRefresh, setModal }) => {
  const openAdd = () => setModal(<AddMeetingForm token={token} onDone={() => { setModal(null); onRefresh(); }} />);
  const openEdit = (mtg) => setModal(<EditMeetingForm item={mtg} token={token} onDone={() => { setModal(null); onRefresh(); }} />);

  const deleteMeeting = async (id) => {
    try { await api(`/meetings/${id}`, token, { method: 'DELETE' }); onRefresh(); toast.success('Meeting deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold" style={{ color: C.text }}>Meeting Notes</h2>
        <button data-testid="add-meeting-btn" onClick={openAdd} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-white transition-all hover:brightness-110" style={{ background: C.amber }}>
          <Plus size={15} /> Add Meeting
        </button>
      </div>
      <div className="space-y-4">
        {items.map(mtg => (
          <div key={mtg.id} data-testid={`meeting-card-${mtg.id}`} className="p-5 rounded-xl bg-white" style={{ border: `1px solid ${C.border}` }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold" style={{ color: C.text }}>{mtg.title}</h3>
                <span className="text-xs" style={{ color: C.light }}>{mtg.date}</span>
              </div>
              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                <button data-testid={`edit-meeting-${mtg.id}`} onClick={() => openEdit(mtg)} className="p-1 rounded hover:bg-slate-100"><Edit3 size={13} style={{ color: C.muted }} /></button>
                <button data-testid={`delete-meeting-${mtg.id}`} onClick={() => deleteMeeting(mtg.id)} className="p-1 rounded hover:bg-red-50"><Trash2 size={13} style={{ color: C.red }} /></button>
              </div>
            </div>
            {mtg.attendees && <div className="mb-3"><span className="text-xs font-bold" style={{ color: C.muted }}>Attendees:</span><p className="text-xs whitespace-pre-wrap mt-1" style={{ color: C.text }}>{mtg.attendees}</p></div>}
            {mtg.key_points && <div><span className="text-xs font-bold" style={{ color: C.muted }}>Key Points:</span><FormattedText text={mtg.key_points} className="text-sm mt-1 leading-relaxed" style={{ color: C.text }} /></div>}
          </div>
        ))}
      </div>
      {items.length === 0 && <EmptyState text="No meeting notes yet" />}
    </div>
  );
};


/* ═══════════════════════════════════════════
   REVENUE CALCULATOR
   ═══════════════════════════════════════════ */
const fmt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(n));

const SliderField = ({ label, value, onChange, min, max, step = 1, unit = '', color = C.blue }) => (
  <div className="mb-4">
    <div className="flex justify-between mb-1.5">
      <span className="text-xs font-medium" style={{ color: C.muted }}>{label}</span>
      <span className="text-sm font-bold" style={{ color: C.text }}>{fmt(value)}{unit}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))}
      className="w-full h-1.5 rounded-full appearance-none cursor-pointer outline-none"
      style={{ background: `linear-gradient(to right, ${color} ${((value - min) / (max - min)) * 100}%, #e2e8f0 ${((value - min) / (max - min)) * 100}%)` }} />
    <div className="flex justify-between mt-0.5"><span className="text-[10px]" style={{ color: C.light }}>{fmt(min)}{unit}</span><span className="text-[10px]" style={{ color: C.light }}>{fmt(max)}{unit}</span></div>
  </div>
);

const ResultRow = ({ label, value, color, bold }) => (
  <div className="flex justify-between items-center py-1.5" style={{ borderBottom: `1px solid ${C.border}` }}>
    <span className={`text-xs ${bold ? 'font-bold' : 'font-medium'}`} style={{ color: C.muted }}>{label}</span>
    <span className={`text-sm ${bold ? 'font-bold' : 'font-semibold'}`} style={{ color: color || C.text }}>ZMW {fmt(value)}</span>
  </div>
);

const RevenueCalculator = ({ token, user, scenarios, onRefresh, setModal }) => {
  const [care365, setCare365] = useState({ hospitals: 3, doctorsPerHospital: 10, patientsPerDoctor: 20, monthlyFee: 80, platformRate: 20, ministryShare: 25 });
  const [finovo, setFinovo] = useState({ numSmes: 1500, monthlyFee: 250, voxitRate: 70 });
  const [swiftguard, setSwiftguard] = useState({ numOrganizations: 10, monthlyFee: 500, voxitRate: 75 });
  const [totalfarm, setTotalfarm] = useState({ numFarmers: 500, monthlyFee: 50, voxitRate: 70 });
  const [baride, setBaride] = useState({ numDrivers: 40, ridesPerDay: 8, avgFare: 35, commissionRate: 3, ministryShare: 25 });
  const [scenarioName, setScenarioName] = useState('');
  const [saving, setSaving] = useState(false);

  const upC = (k, v) => setCare365(s => ({ ...s, [k]: v }));
  const upF = (k, v) => setFinovo(s => ({ ...s, [k]: v }));
  const upS = (k, v) => setSwiftguard(s => ({ ...s, [k]: v }));
  const upT = (k, v) => setTotalfarm(s => ({ ...s, [k]: v }));
  const upB = (k, v) => setBaride(s => ({ ...s, [k]: v }));

  // ── Care365 Calculations ──
  const c_totalDoctors = care365.hospitals * care365.doctorsPerHospital;
  const c_totalPatients = c_totalDoctors * care365.patientsPerDoctor;
  const c_monthlyRev = c_totalPatients * care365.monthlyFee;
  const c_commission = c_monthlyRev * (care365.platformRate / 100);
  const c_voxit = c_commission * (1 - care365.ministryShare / 100);
  const c_ministry = c_commission * (care365.ministryShare / 100);

  // ── Finovo Calculations ──
  const f_monthlyRev = finovo.numSmes * finovo.monthlyFee;
  const f_voxit = f_monthlyRev * (finovo.voxitRate / 100);
  const f_ministry = f_monthlyRev * ((100 - finovo.voxitRate) / 100);

  // ── SwiftGuard Calculations ──
  const s_monthlyRev = swiftguard.numOrganizations * swiftguard.monthlyFee;
  const s_voxit = s_monthlyRev * (swiftguard.voxitRate / 100);
  const s_ministry = s_monthlyRev * ((100 - swiftguard.voxitRate) / 100);

  // ── TotalFarm / SmartFarm Pro Calculations ──
  const t_monthlyRev = totalfarm.numFarmers * totalfarm.monthlyFee;
  const t_voxit = t_monthlyRev * (totalfarm.voxitRate / 100);
  const t_ministry = t_monthlyRev * ((100 - totalfarm.voxitRate) / 100);

  // ── BaRide Calculations ──
  const b_monthlyRides = baride.numDrivers * baride.ridesPerDay * 30;
  const b_monthlyGross = b_monthlyRides * baride.avgFare;
  const b_commission = b_monthlyGross * (baride.commissionRate / 100);
  const b_voxit = b_commission * (1 - baride.ministryShare / 100);
  const b_ministry = b_commission * (baride.ministryShare / 100);

  // ── Combined ──
  const totalMonthly = c_monthlyRev + f_monthlyRev + s_monthlyRev + t_monthlyRev + b_monthlyGross;
  const totalVoxit = c_voxit + f_voxit + s_voxit + t_voxit + b_voxit;
  const totalMinistry = c_ministry + f_ministry + s_ministry + t_ministry + b_ministry;

  const saveScenario = async () => {
    if (!scenarioName.trim()) { toast.error('Enter a scenario name'); return; }
    setSaving(true);
    try {
      await api('/revenue-scenarios', token, { method: 'POST', body: JSON.stringify({ name: scenarioName, care365, finovo, swiftguard, totalfarm, baride }) });
      toast.success('Scenario saved'); setScenarioName(''); onRefresh();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const loadScenario = (sc) => {
    if (sc.care365) setCare365(s => ({ ...s, ...sc.care365 }));
    if (sc.finovo) setFinovo(s => ({ ...s, ...sc.finovo }));
    if (sc.swiftguard) setSwiftguard(s => ({ ...s, ...sc.swiftguard }));
    if (sc.totalfarm) setTotalfarm(s => ({ ...s, ...sc.totalfarm }));
    if (sc.baride) setBaride(s => ({ ...s, ...sc.baride }));
    toast.success(`Loaded: ${sc.name}`);
  };

  const deleteScenario = async (id) => {
    try { await api(`/revenue-scenarios/${id}`, token, { method: 'DELETE' }); onRefresh(); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="p-5 rounded-xl bg-white text-center" style={{ border: `1px solid ${C.border}` }}>
          <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: C.muted }}>Total Monthly Revenue</div>
          <div className="text-2xl font-bold" style={{ color: C.text }}>ZMW {fmt(totalMonthly)}</div>
          <div className="text-xs mt-1" style={{ color: C.light }}>Annual: ZMW {fmt(totalMonthly * 12)}</div>
        </div>
        <div className="p-5 rounded-xl text-center" style={{ background: C.blueBg, border: `1px solid ${C.blue}30` }}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src={VOXIT_LOGO} alt="VoxIT" className="h-4 object-contain opacity-70" />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: C.blue }}>VoxIT Share</span>
          </div>
          <div className="text-2xl font-bold" style={{ color: C.blue }}>ZMW {fmt(totalVoxit)}</div>
          <div className="text-xs mt-1" style={{ color: C.blue }}>Annual: ZMW {fmt(totalVoxit * 12)}</div>
        </div>
        <div className="p-5 rounded-xl text-center" style={{ background: C.greenBg, border: `1px solid ${C.green}30` }}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src={SME_LOGO} alt="Ministry" className="h-5 object-contain opacity-70" />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: C.green }}>Ministry Share</span>
          </div>
          <div className="text-2xl font-bold" style={{ color: C.green }}>ZMW {fmt(totalMinistry)}</div>
          <div className="text-xs mt-1" style={{ color: C.green }}>Annual: ZMW {fmt(totalMinistry * 12)}</div>
        </div>
      </div>

      {/* Win-Win Bar */}
      <div className="mb-6 p-4 rounded-xl bg-white" style={{ border: `1px solid ${C.border}` }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold" style={{ color: C.text }}>Revenue Distribution</span>
          <span className="text-xs" style={{ color: C.muted }}>Monthly: ZMW {fmt(totalMonthly)}</span>
        </div>
        <div className="flex h-5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
          {totalMonthly > 0 && <>
            <div className="flex items-center justify-center text-[10px] font-bold text-white transition-all" style={{ width: `${(totalVoxit / totalMonthly) * 100}%`, background: C.blue }}>
              {((totalVoxit / totalMonthly) * 100).toFixed(0)}%
            </div>
            <div className="flex items-center justify-center text-[10px] font-bold text-white transition-all" style={{ width: `${(totalMinistry / totalMonthly) * 100}%`, background: C.green }}>
              {((totalMinistry / totalMonthly) * 100).toFixed(0)}%
            </div>
            {(totalMonthly - totalVoxit - totalMinistry) > 1 && (
              <div className="flex items-center justify-center text-[10px] font-bold transition-all" style={{ width: `${((totalMonthly - totalVoxit - totalMinistry) / totalMonthly) * 100}%`, background: '#94a3b8', color: 'white' }}>
                Doctors
              </div>
            )}
          </>}
        </div>
        <div className="flex items-center justify-center gap-6 mt-2">
          <span className="flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.blue }} /> VoxIT</span>
          <span className="flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.green }} /> Ministry</span>
          <span className="flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#94a3b8' }} /> Doctors/Providers</span>
        </div>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">

        {/* ── Care365 ── */}
        <div className="p-5 rounded-xl bg-white" style={{ border: `1px solid ${C.border}`, borderTop: `3px solid ${C.green}` }}>
          <div className="flex items-center gap-2 mb-4">
            <Stethoscope size={18} style={{ color: C.green }} />
            <span className="text-sm font-bold" style={{ color: C.text }}>Care365 — Telemedicine</span>
          </div>
          <SliderField label="Number of Hospitals" value={care365.hospitals} onChange={v => upC('hospitals', v)} min={1} max={20} color={C.green} />
          <SliderField label="Doctors / Nurses per Hospital" value={care365.doctorsPerHospital} onChange={v => upC('doctorsPerHospital', v)} min={1} max={50} color={C.green} />
          <SliderField label="Patients per Doctor per Month" value={care365.patientsPerDoctor} onChange={v => upC('patientsPerDoctor', v)} min={5} max={100} color={C.green} />
          <SliderField label="Monthly Fee per Patient" value={care365.monthlyFee} onChange={v => upC('monthlyFee', v)} min={20} max={500} step={10} unit=" ZMW" color={C.green} />
          <SliderField label="Platform Commission" value={care365.platformRate} onChange={v => upC('platformRate', v)} min={5} max={50} unit="%" color={C.green} />
          <SliderField label="Ministry Share of Commission" value={care365.ministryShare} onChange={v => upC('ministryShare', v)} min={5} max={50} unit="%" color={C.green} />
          <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
            <div className="text-xs mb-2" style={{ color: C.light }}>{c_totalDoctors} doctors x {care365.patientsPerDoctor} patients = {fmt(c_totalPatients)} patients</div>
            <ResultRow label="Monthly Patient Revenue" value={c_monthlyRev} />
            <ResultRow label="Platform Commission" value={c_commission} />
            <ResultRow label="VoxIT Share" value={c_voxit} color={C.blue} bold />
            <ResultRow label="Ministry Share" value={c_ministry} color={C.green} bold />
          </div>
        </div>

        {/* ── Finovo ── */}
        <div className="p-5 rounded-xl bg-white" style={{ border: `1px solid ${C.border}`, borderTop: `3px solid ${C.blue}` }}>
          <div className="flex items-center gap-2 mb-4">
            <Calculator size={18} style={{ color: C.blue }} />
            <span className="text-sm font-bold" style={{ color: C.text }}>Finovo — AI Accounting</span>
          </div>
          <SliderField label="Number of SMEs" value={finovo.numSmes} onChange={v => upF('numSmes', v)} min={100} max={10000} step={100} color={C.blue} />
          <SliderField label="Monthly Fee per SME" value={finovo.monthlyFee} onChange={v => upF('monthlyFee', v)} min={50} max={1000} step={10} unit=" ZMW" color={C.blue} />
          <SliderField label="VoxIT Share" value={finovo.voxitRate} onChange={v => upF('voxitRate', v)} min={50} max={90} unit="%" color={C.blue} />
          <div className="text-xs mb-2 text-right" style={{ color: C.green }}>Ministry: {100 - finovo.voxitRate}%</div>
          <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
            <div className="text-xs mb-2" style={{ color: C.light }}>{fmt(finovo.numSmes)} SMEs x {finovo.monthlyFee} ZMW</div>
            <ResultRow label="Monthly Subscription Revenue" value={f_monthlyRev} />
            <ResultRow label="VoxIT Share" value={f_voxit} color={C.blue} bold />
            <ResultRow label="Ministry Share" value={f_ministry} color={C.green} bold />
          </div>
        </div>

        {/* ── SwiftGuard ── */}
        <div className="p-5 rounded-xl bg-white" style={{ border: `1px solid ${C.border}`, borderTop: `3px solid ${C.red}` }}>
          <div className="flex items-center gap-2 mb-4">
            <Shield size={18} style={{ color: C.red }} />
            <span className="text-sm font-bold" style={{ color: C.text }}>SwiftGuard — Emergency SOS</span>
          </div>
          <SliderField label="Number of Organizations" value={swiftguard.numOrganizations} onChange={v => upS('numOrganizations', v)} min={1} max={100} color={C.red} />
          <SliderField label="Monthly Fee per Organization" value={swiftguard.monthlyFee} onChange={v => upS('monthlyFee', v)} min={100} max={5000} step={50} unit=" ZMW" color={C.red} />
          <SliderField label="VoxIT Share" value={swiftguard.voxitRate} onChange={v => upS('voxitRate', v)} min={50} max={90} unit="%" color={C.red} />
          <div className="text-xs mb-2 text-right" style={{ color: C.green }}>Ministry: {100 - swiftguard.voxitRate}%</div>
          <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
            <div className="text-xs mb-2" style={{ color: C.light }}>{swiftguard.numOrganizations} orgs x {fmt(swiftguard.monthlyFee)} ZMW</div>
            <ResultRow label="Monthly Revenue" value={s_monthlyRev} />
            <ResultRow label="VoxIT Share" value={s_voxit} color={C.blue} bold />
            <ResultRow label="Ministry Share" value={s_ministry} color={C.green} bold />
          </div>
        </div>

        {/* ── SmartFarm Pro / TotalFarm ── */}
        <div className="p-5 rounded-xl bg-white" style={{ border: `1px solid ${C.border}`, borderTop: `3px solid #16a34a` }}>
          <div className="flex items-center gap-2 mb-4">
            <Leaf size={18} style={{ color: '#16a34a' }} />
            <span className="text-sm font-bold" style={{ color: C.text }}>SmartFarm Pro — Farm Management</span>
          </div>
          <SliderField label="Number of Farmers / Cooperatives" value={totalfarm.numFarmers} onChange={v => upT('numFarmers', v)} min={50} max={5000} step={50} color="#16a34a" />
          <SliderField label="Monthly Subscription Fee" value={totalfarm.monthlyFee} onChange={v => upT('monthlyFee', v)} min={10} max={300} step={5} unit=" ZMW" color="#16a34a" />
          <SliderField label="VoxIT Share" value={totalfarm.voxitRate} onChange={v => upT('voxitRate', v)} min={50} max={90} unit="%" color="#16a34a" />
          <div className="text-xs mb-2 text-right" style={{ color: C.green }}>Ministry: {100 - totalfarm.voxitRate}%</div>
          <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
            <div className="text-xs mb-2" style={{ color: C.light }}>{fmt(totalfarm.numFarmers)} farmers x {totalfarm.monthlyFee} ZMW</div>
            <ResultRow label="Monthly Subscription Revenue" value={t_monthlyRev} />
            <ResultRow label="VoxIT Share" value={t_voxit} color={C.blue} bold />
            <ResultRow label="Ministry Share" value={t_ministry} color={C.green} bold />
          </div>
        </div>

        {/* ── BaRide ── */}
        <div className="p-5 rounded-xl bg-white" style={{ border: `1px solid ${C.border}`, borderTop: `3px solid ${C.purple}` }}>
          <div className="flex items-center gap-2 mb-4">
            <Car size={18} style={{ color: C.purple }} />
            <span className="text-sm font-bold" style={{ color: C.text }}>BaRide — Transport Platform</span>
          </div>
          <SliderField label="Number of Active Drivers" value={baride.numDrivers} onChange={v => upB('numDrivers', v)} min={10} max={500} step={5} color={C.purple} />
          <SliderField label="Rides per Driver per Day" value={baride.ridesPerDay} onChange={v => upB('ridesPerDay', v)} min={1} max={30} color={C.purple} />
          <SliderField label="Average Fare per Ride" value={baride.avgFare} onChange={v => upB('avgFare', v)} min={10} max={200} step={5} unit=" ZMW" color={C.purple} />
          <SliderField label="Platform Commission (Drivers keep rest)" value={baride.commissionRate} onChange={v => upB('commissionRate', v)} min={1} max={10} unit="%" color={C.purple} />
          <SliderField label="Ministry Share of Commission" value={baride.ministryShare} onChange={v => upB('ministryShare', v)} min={5} max={50} unit="%" color={C.purple} />
          <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
            <div className="text-xs mb-2" style={{ color: C.light }}>{baride.numDrivers} drivers x {baride.ridesPerDay} rides x {baride.avgFare} ZMW x 30 days = {fmt(b_monthlyRides)} rides/mo</div>
            <ResultRow label="Monthly Gross Fare Revenue" value={b_monthlyGross} />
            <ResultRow label={`Platform Commission (${baride.commissionRate}%)`} value={b_commission} />
            <ResultRow label="VoxIT Share" value={b_voxit} color={C.blue} bold />
            <ResultRow label="Ministry Share" value={b_ministry} color={C.green} bold />
            <div className="text-xs mt-2 pt-2 font-medium" style={{ color: C.purple, borderTop: `1px dashed ${C.border}` }}>
              Drivers keep: ZMW {fmt(b_monthlyGross - b_commission)}/mo ({100 - baride.commissionRate}%)
            </div>
          </div>
        </div>
      </div>

      {/* Annual Summary */}
      <div className="p-5 rounded-xl bg-white mb-6" style={{ border: `1px solid ${C.border}` }}>
        <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: C.text }}>Annual Projection Summary</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Care365', monthly: c_monthlyRev, voxit: c_voxit, ministry: c_ministry, color: C.green },
            { label: 'Finovo', monthly: f_monthlyRev, voxit: f_voxit, ministry: f_ministry, color: C.blue },
            { label: 'SwiftGuard', monthly: s_monthlyRev, voxit: s_voxit, ministry: s_ministry, color: C.red },
            { label: 'SmartFarm', monthly: t_monthlyRev, voxit: t_voxit, ministry: t_ministry, color: '#16a34a' },
            { label: 'BaRide', monthly: b_monthlyGross, voxit: b_voxit, ministry: b_ministry, color: C.purple },
            { label: 'COMBINED', monthly: totalMonthly, voxit: totalVoxit, ministry: totalMinistry, color: C.text },
          ].map(p => (
            <div key={p.label} className="p-3 rounded-lg" style={{ background: p.label === 'COMBINED' ? C.purpleBg : C.bg, border: `1px solid ${C.border}` }}>
              <div className="text-xs font-bold mb-2" style={{ color: p.color }}>{p.label}</div>
              <div className="text-xs mb-1" style={{ color: C.muted }}>Monthly: <span className="font-semibold" style={{ color: C.text }}>ZMW {fmt(p.monthly)}</span></div>
              <div className="text-xs mb-1" style={{ color: C.muted }}>Annual: <span className="font-semibold" style={{ color: C.text }}>ZMW {fmt(p.monthly * 12)}</span></div>
              <div className="text-xs" style={{ color: C.blue }}>VoxIT: ZMW {fmt(p.voxit * 12)}/yr</div>
              <div className="text-xs" style={{ color: C.green }}>Ministry: ZMW {fmt(p.ministry * 12)}/yr</div>
            </div>
          ))}
        </div>
      </div>

      {/* Save / Load Scenarios */}
      <div className="p-5 rounded-xl bg-white" style={{ border: `1px solid ${C.border}` }}>
        <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: C.text }}>Save & Load Scenarios</h3>
        <div className="flex items-center gap-2 mb-4">
          <input value={scenarioName} onChange={e => setScenarioName(e.target.value)} placeholder="Scenario name..."
            className="flex-1 px-3 py-2 rounded-lg outline-none text-sm" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }} />
          <button onClick={saveScenario} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.blue }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
        {scenarios.length > 0 && (
          <div className="space-y-2">
            {scenarios.map(sc => (
              <div key={sc.id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: C.bg }}>
                <div>
                  <span className="text-sm font-medium" style={{ color: C.text }}>{sc.name}</span>
                  <span className="text-xs ml-2" style={{ color: C.light }}>by {sc.created_by?.name} - {new Date(sc.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => loadScenario(sc)} className="text-xs px-3 py-1 rounded-md font-medium text-white" style={{ background: C.blue }}>Load</button>
                  <button onClick={() => deleteScenario(sc.id)} className="text-xs px-2 py-1 rounded-md font-medium" style={{ background: '#fef2f2', color: C.red }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════
   ACTIVITY LOG VIEW (VoxIT only)
   ═══════════════════════════════════════════ */
const ActivityLogView = ({ items }) => (
  <div>
    <h2 className="text-lg font-bold mb-4" style={{ color: C.text }}>Login Activity</h2>
    <div className="bg-white rounded-xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
      <table className="w-full">
        <thead><tr style={{ background: C.bg }}>
          <th className="text-xs font-semibold text-left px-4 py-3" style={{ color: C.muted }}>Name</th>
          <th className="text-xs font-semibold text-left px-4 py-3" style={{ color: C.muted }}>Role</th>
          <th className="text-xs font-semibold text-left px-4 py-3" style={{ color: C.muted }}>Logged In</th>
        </tr></thead>
        <tbody>
          {items.map((log, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
              <td className="text-sm px-4 py-3" style={{ color: C.text }}>{log.name}</td>
              <td className="px-4 py-3"><RoleBadge role={log.role} /></td>
              <td className="text-xs px-4 py-3" style={{ color: C.muted }}>{new Date(log.logged_in_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length === 0 && <div className="text-center py-8 text-xs" style={{ color: C.light }}>No login activity yet</div>}
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   MODALS / FORMS
   ═══════════════════════════════════════════ */
const ModalOverlay = ({ children, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
    <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()}
      className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
      {children}
    </motion.div>
  </motion.div>
);

const FormField = ({ label, children }) => (
  <div className="mb-3"><label className="text-xs font-medium mb-1 block" style={{ color: C.muted }}>{label}</label>{children}</div>
);

const inputCls = "w-full px-3 py-2.5 rounded-lg outline-none text-sm";
const inputStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text };

/* Rich Text Area with formatting toolbar */
const RichTextArea = ({ value, onChange, rows = 4, placeholder = '' }) => {
  const ref = React.useRef(null);
  const insert = (before, after = '') => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.substring(start, end);
    const newText = value.substring(0, start) + before + selected + after + value.substring(end);
    onChange(newText);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + before.length, start + before.length + selected.length); }, 0);
  };
  const wrapLines = (prefix) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.substring(start, end);
    if (selected) {
      const lines = selected.split('\n').map(l => `${prefix}${l}`).join('\n');
      const newText = value.substring(0, start) + lines + value.substring(end);
      onChange(newText);
    } else {
      insert('\n' + prefix);
    }
  };
  return (
    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
      <div className="flex items-center gap-0.5 px-2 py-1.5" style={{ background: '#f1f5f9', borderBottom: `1px solid ${C.border}` }}>
        <button type="button" onClick={() => insert('**', '**')} className="px-2 py-1 rounded text-xs font-bold hover:bg-white transition-all" style={{ color: C.text }} title="Bold">B</button>
        <button type="button" onClick={() => insert('*', '*')} className="px-2 py-1 rounded text-xs italic hover:bg-white transition-all" style={{ color: C.text }} title="Italic">I</button>
        <span className="w-px h-4 mx-1" style={{ background: C.border }} />
        <button type="button" onClick={() => wrapLines('- ')} className="px-2 py-1 rounded text-xs hover:bg-white transition-all" style={{ color: C.text }} title="Bullet list">&#8226; List</button>
        <button type="button" onClick={() => wrapLines('1. ')} className="px-2 py-1 rounded text-xs hover:bg-white transition-all" style={{ color: C.text }} title="Numbered list">1. List</button>
        <span className="w-px h-4 mx-1" style={{ background: C.border }} />
        <button type="button" onClick={() => insert('\n## ', '\n')} className="px-2 py-1 rounded text-xs font-bold hover:bg-white transition-all" style={{ color: C.text }} title="Heading">H</button>
      </div>
      <textarea ref={ref} value={value} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
        className="w-full px-3 py-2.5 outline-none text-sm resize-y" style={{ background: C.bg, color: C.text, border: 'none' }} />
    </div>
  );
};

/* Render formatted text (markdown-lite) */
const FormattedText = ({ text, className = '', style = {} }) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let listType = null;

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === 'ul') elements.push(<ul key={`ul-${elements.length}`} className="list-disc list-inside space-y-0.5 my-1">{listItems}</ul>);
      else elements.push(<ol key={`ol-${elements.length}`} className="list-decimal list-inside space-y-0.5 my-1">{listItems}</ol>);
      listItems = [];
      listType = null;
    }
  };

  const renderInline = (line) => {
    const parts = [];
    let remaining = line;
    let key = 0;
    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      const italicMatch = remaining.match(/\*(.+?)\*/);
      const match = boldMatch && (!italicMatch || boldMatch.index <= italicMatch.index) ? boldMatch : italicMatch;
      if (match) {
        if (match.index > 0) parts.push(<span key={key++}>{remaining.substring(0, match.index)}</span>);
        if (match[0].startsWith('**')) parts.push(<strong key={key++} className="font-bold">{match[1]}</strong>);
        else parts.push(<em key={key++} className="italic">{match[1]}</em>);
        remaining = remaining.substring(match.index + match[0].length);
      } else {
        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }
    }
    return parts;
  };

  lines.forEach((line, i) => {
    if (/^## (.+)/.test(line)) { flushList(); elements.push(<div key={i} className="font-bold text-sm mt-2 mb-1" style={{ color: C.text }}>{renderInline(line.replace(/^## /, ''))}</div>); }
    else if (/^- (.+)/.test(line)) { if (listType !== 'ul') flushList(); listType = 'ul'; listItems.push(<li key={i}>{renderInline(line.replace(/^- /, ''))}</li>); }
    else if (/^\d+\. (.+)/.test(line)) { if (listType !== 'ol') flushList(); listType = 'ol'; listItems.push(<li key={i}>{renderInline(line.replace(/^\d+\. /, ''))}</li>); }
    else if (line.trim() === '') { flushList(); elements.push(<div key={i} className="h-2" />); }
    else { flushList(); elements.push(<div key={i}>{renderInline(line)}</div>); }
  });
  flushList();

  return <div className={className} style={style}>{elements}</div>;
};

const AddTaskForm = ({ token, user, onDone }) => {
  const [form, setForm] = useState({ title: '', description: '', assigned_to: user.role, project: 'general', deadline: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api('/tasks', token, { method: 'POST', body: JSON.stringify(form) }); toast.success('Task created'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-4" style={{ color: C.text }}>New Task</h3>
      <FormField label="Title"><input data-testid="task-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Description"><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Deadline"><input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Project">
        <select value={form.project} onChange={e => setForm(f => ({ ...f, project: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="care365">Care365</option><option value="finovo">Finovo</option><option value="swiftguard">SwiftGuard</option>
          <option value="smartfarm">SmartFarm Pro</option><option value="baride">BaRide</option><option value="sme_database">SME Database</option><option value="banking_platform">Banking Platform</option><option value="general">General</option>
        </select>
      </FormField>
      <FormField label="Assigned To">
        <select value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="voxit">VoxIT</option><option value="ministry">Ministry of SME</option>
        </select>
      </FormField>
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.blue }}>
        {saving ? 'Creating...' : 'Create Task'}
      </button>
    </form>
  );
};

const EditTaskForm = ({ task, token, onDone }) => {
  const [form, setForm] = useState({ title: task.title, description: task.description || '', assigned_to: task.assigned_to, status: task.status, project: task.project || 'general', deadline: task.deadline || '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api(`/tasks/${task.id}`, token, { method: 'PUT', body: JSON.stringify(form) }); toast.success('Task updated'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-4" style={{ color: C.text }}>Edit Task</h3>
      <FormField label="Title"><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Description"><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Deadline"><input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Project">
        <select value={form.project} onChange={e => setForm(f => ({ ...f, project: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="care365">Care365</option><option value="finovo">Finovo</option><option value="swiftguard">SwiftGuard</option>
          <option value="smartfarm">SmartFarm Pro</option><option value="baride">BaRide</option><option value="sme_database">SME Database</option><option value="banking_platform">Banking Platform</option><option value="general">General</option>
        </select>
      </FormField>
      <FormField label="Status">
        <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="todo">To Do</option><option value="in_progress">In Progress</option><option value="done">Done</option>
        </select>
      </FormField>
      <FormField label="Assigned To">
        <select value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="voxit">VoxIT</option><option value="ministry">Ministry of SME</option>
        </select>
      </FormField>
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.blue }}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
};

const AddCommentForm = ({ task, token, user, onDone }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api(`/tasks/${task.id}/comments`, token, { method: 'POST', body: JSON.stringify({ text }) }); toast.success('Comment added'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-1" style={{ color: C.text }}>Add Comment</h3>
      <p className="text-xs mb-4" style={{ color: C.muted }}>on: {task.title}</p>
      {task.comments?.length > 0 && (
        <div className="mb-4 space-y-1.5 max-h-40 overflow-y-auto">
          {task.comments.map(c => (
            <div key={c.id} className="text-xs p-2 rounded" style={{ background: C.bg }}>
              <span className="font-semibold" style={{ color: c.author?.role === 'voxit' ? C.blue : C.green }}>{c.author?.name}:</span>
              <span className="ml-1" style={{ color: C.muted }}>{c.text}</span>
            </div>
          ))}
        </div>
      )}
      <textarea data-testid="comment-input" value={text} onChange={e => setText(e.target.value)} required rows={3} placeholder="Write your comment..." className={inputCls} style={inputStyle} />
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-3 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.blue }}>
        {saving ? 'Posting...' : 'Post Comment'}
      </button>
    </form>
  );
};

const AddInitiativeForm = ({ token, onDone }) => {
  const [form, setForm] = useState({ title: '', description: '', status: 'Planning', project: 'general', deadline: '', voxit_action: '', ministry_action: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api('/initiatives', token, { method: 'POST', body: JSON.stringify(form) }); toast.success('Initiative created'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-4" style={{ color: C.text }}>New Initiative</h3>
      <FormField label="Title"><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Project">
        <select value={form.project} onChange={e => setForm(f => ({ ...f, project: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="care365">Care365</option><option value="finovo">Finovo</option><option value="swiftguard">SwiftGuard</option>
          <option value="smartfarm">SmartFarm Pro</option><option value="baride">BaRide</option><option value="sme_database">SME Database</option><option value="banking_platform">Banking Platform</option><option value="general">General</option>
        </select>
      </FormField>
      <FormField label="Description"><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Status"><input value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Deadline"><input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="VoxIT Action"><input value={form.voxit_action} onChange={e => setForm(f => ({ ...f, voxit_action: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Ministry Action"><input value={form.ministry_action} onChange={e => setForm(f => ({ ...f, ministry_action: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.green }}>
        {saving ? 'Creating...' : 'Create Initiative'}
      </button>
    </form>
  );
};

const EditInitiativeForm = ({ item, token, onDone }) => {
  const [form, setForm] = useState({ title: item.title, description: item.description || '', status: item.status || '', project: item.project || 'general', deadline: item.deadline || '', voxit_action: item.voxit_action || '', ministry_action: item.ministry_action || '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api(`/initiatives/${item.id}`, token, { method: 'PUT', body: JSON.stringify(form) }); toast.success('Updated'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-4" style={{ color: C.text }}>Edit Initiative</h3>
      <FormField label="Title"><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Project">
        <select value={form.project} onChange={e => setForm(f => ({ ...f, project: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="care365">Care365</option><option value="finovo">Finovo</option><option value="swiftguard">SwiftGuard</option>
          <option value="smartfarm">SmartFarm Pro</option><option value="baride">BaRide</option><option value="sme_database">SME Database</option><option value="banking_platform">Banking Platform</option><option value="general">General</option>
        </select>
      </FormField>
      <FormField label="Description"><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Status"><input value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Deadline"><input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="VoxIT Action"><input value={form.voxit_action} onChange={e => setForm(f => ({ ...f, voxit_action: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Ministry Action"><input value={form.ministry_action} onChange={e => setForm(f => ({ ...f, ministry_action: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.green }}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
};

const AddRequirementForm = ({ token, onDone }) => {
  const [form, setForm] = useState({ title: '', description: '', project: 'sme_database', priority: 'medium' });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState('');
  const fileInputRef = React.useRef(null);

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const valid = [];
    for (const f of picked) {
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_UPLOAD_EXTS.includes(ext)) {
        toast.error(`${f.name}: file type .${ext} not allowed`);
        continue;
      }
      if (f.size > MAX_UPLOAD_SIZE_BYTES) {
        toast.error(`${f.name}: file too large (max 25 MB)`);
        continue;
      }
      valid.push(f);
    }
    setFiles(prev => [...prev, ...valid]);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const created = await api('/requirements', token, { method: 'POST', body: JSON.stringify(form) });
      // Upload each file, sequentially so the user sees progress
      for (let i = 0; i < files.length; i++) {
        setProgress(`Uploading file ${i + 1}/${files.length}: ${files[i].name}`);
        const fd = new FormData();
        fd.append('file', files[i]);
        const res = await fetch(`${API_URL}/api/sme-workspace/requirements/${created.id}/attachments`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Failed to upload ${files[i].name}`);
        }
      }
      toast.success(files.length > 0 ? `Requirement submitted with ${files.length} file${files.length > 1 ? 's' : ''}` : 'Requirement submitted');
      onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
      setProgress('');
    }
  };

  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-4" style={{ color: C.text }}>Submit Requirement</h3>
      <FormField label="Project">
        <select value={form.project} onChange={e => setForm(f => ({ ...f, project: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="sme_database">SME Database Platform</option>
          <option value="banking_platform">Banking Products Platform</option>
          <option value="other">Other</option>
        </select>
      </FormField>
      <FormField label="Title"><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="Brief title of the requirement" className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Description"><RichTextArea value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} rows={5} placeholder="Describe what you need, how it should work, what data to capture..." /></FormField>
      <FormField label="Priority">
        <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
      </FormField>
      <FormField label="Attachments (optional)">
        <div className="space-y-2">
          <button
            type="button"
            data-testid="add-requirement-files-btn"
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all hover:brightness-95"
            style={{ background: C.purpleBg, color: C.purple, border: `1px dashed ${C.purple}66` }}
          >
            <Paperclip size={14} /> {files.length === 0 ? 'Attach Word, PDF, Excel, PNG or JPG' : 'Add another file'}
          </button>
          <input ref={fileInputRef} type="file" accept={ALLOWED_UPLOAD_ACCEPT} multiple onChange={addFiles} className="hidden" data-testid="add-requirement-file-input" />
          {files.length > 0 && (
            <div className="space-y-1.5">
              {files.map((f, i) => {
                const ext = (f.name.split('.').pop() || '').toLowerCase();
                return (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                    <span className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'white', border: `1px solid ${C.border}` }}>
                      <FileText size={13} style={{ color: fileIconColor(ext) }} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold truncate" style={{ color: C.text }}>{f.name}</div>
                      <div className="text-[10px]" style={{ color: C.light }}>{formatFileSize(f.size)}</div>
                    </div>
                    <button type="button" onClick={() => removeFile(i)} className="p-1 rounded hover:bg-red-50">
                      <X size={13} style={{ color: C.red }} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[10px]" style={{ color: C.light }}>Allowed: .docx, .doc, .pdf, .xlsx, .xls, .png, .jpg · max 25 MB per file</p>
        </div>
      </FormField>
      {progress && <p className="text-xs mb-2 text-center" style={{ color: C.muted }}>{progress}</p>}
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.purple }}>
        {saving ? (progress || 'Submitting...') : 'Submit Requirement'}
      </button>
    </form>
  );
};

const RespondForm = ({ item, token, onDone }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api(`/requirements/${item.id}/respond`, token, { method: 'POST', body: JSON.stringify({ text }) }); toast.success('Response added'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-1" style={{ color: C.text }}>Respond to Requirement</h3>
      <p className="text-xs mb-4" style={{ color: C.muted }}>{item.title}</p>
      <RichTextArea value={text} onChange={v => setText(v)} rows={4} placeholder="Type your response..." />
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-3 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.purple }}>
        {saving ? 'Sending...' : 'Send Response'}
      </button>
    </form>
  );
};

const AddMeetingForm = ({ token, onDone }) => {
  const [form, setForm] = useState({ title: '', date: '', attendees: '', key_points: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api('/meetings', token, { method: 'POST', body: JSON.stringify(form) }); toast.success('Meeting notes added'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-4" style={{ color: C.text }}>Add Meeting Notes</h3>
      <FormField label="Title"><input data-testid="meeting-title-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="Meeting title" className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Date"><input value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} placeholder="e.g. April 2025" className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Attendees"><textarea value={form.attendees} onChange={e => setForm(f => ({ ...f, attendees: e.target.value }))} rows={2} placeholder="List attendees..." className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Key Points"><RichTextArea value={form.key_points} onChange={v => setForm(f => ({ ...f, key_points: v }))} rows={5} placeholder="Key discussion points and outcomes..." /></FormField>
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.amber }}>
        {saving ? 'Saving...' : 'Save Meeting Notes'}
      </button>
    </form>
  );
};

const EditMeetingForm = ({ item, token, onDone }) => {
  const [form, setForm] = useState({ title: item.title || '', date: item.date || '', attendees: item.attendees || '', key_points: item.key_points || '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api(`/meetings/${item.id}`, token, { method: 'PUT', body: JSON.stringify(form) }); toast.success('Meeting updated'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-4" style={{ color: C.text }}>Edit Meeting</h3>
      <FormField label="Title"><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Date"><input value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Attendees"><textarea value={form.attendees} onChange={e => setForm(f => ({ ...f, attendees: e.target.value }))} rows={2} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Key Points"><RichTextArea value={form.key_points} onChange={v => setForm(f => ({ ...f, key_points: v }))} rows={5} /></FormField>
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.amber }}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
};

const EditRequirementForm = ({ item, token, onDone }) => {
  const [form, setForm] = useState({ title: item.title || '', description: item.description || '', project: item.project || 'sme_database', priority: item.priority || 'medium' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api(`/requirements/${item.id}`, token, { method: 'PUT', body: JSON.stringify(form) }); toast.success('Requirement updated'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-4" style={{ color: C.text }}>Edit Requirement</h3>
      <FormField label="Project">
        <select value={form.project} onChange={e => setForm(f => ({ ...f, project: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="sme_database">SME Database Platform</option>
          <option value="banking_platform">Banking Products Platform</option>
          <option value="other">Other</option>
        </select>
      </FormField>
      <FormField label="Title"><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Description"><RichTextArea value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} rows={5} /></FormField>
      <FormField label="Priority">
        <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
      </FormField>
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: C.purple }}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
};

const DemoSelector = () => {
  const [activeDemo, setActiveDemo] = useState(null);
  if (activeDemo === 'crossbase') return <div className="h-full"><button onClick={() => setActiveDemo(null)} className="fixed top-[110px] right-4 z-50 text-[10px] font-semibold px-3 py-1.5 rounded-lg bg-white shadow-md" style={{ color: C.muted }}>Back to Demos</button><CrossbaseDemo /></div>;
  if (activeDemo === 'finance') return <div className="h-full"><button onClick={() => setActiveDemo(null)} className="fixed top-[110px] right-4 z-50 text-[10px] font-semibold px-3 py-1.5 rounded-lg bg-white shadow-md" style={{ color: C.muted }}>Back to Demos</button><CrossbaseFinanceDemo /></div>;
  return (
    <div className="max-w-3xl mx-auto py-8">
      <h2 className="text-lg font-bold mb-1" style={{ color: C.text }}>Platform Demos</h2>
      <p className="text-xs mb-6" style={{ color: C.muted }}>Clickable prototypes showing how each platform will look and function</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div onClick={() => setActiveDemo('crossbase')} className="p-6 rounded-xl bg-white cursor-pointer hover:shadow-lg transition-all" style={{ border: '2px solid #0d7a3e', borderTop: '4px solid #0d7a3e' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: '#0d7a3e15' }}><Users size={24} style={{ color: '#0d7a3e' }} /></div>
          <h3 className="text-base font-bold mb-1" style={{ color: C.text }}>Demo 1: CROSSBASE</h3>
          <p className="text-sm font-medium mb-2" style={{ color: '#0d7a3e' }}>Zambia SME Digital Platform</p>
          <p className="text-xs leading-relaxed mb-3" style={{ color: C.muted }}>National SME registration, digital database, Ministry analytics dashboard, and redesigned public website.</p>
          <div className="flex flex-wrap gap-1">{['Public Website','SME Portal','Ministry Dashboard','Enterprise Map','Reports'].map(f => (<span key={f} className="text-[9px] px-2 py-0.5 rounded-full font-medium" style={{ background: '#0d7a3e10', color: '#0d7a3e' }}>{f}</span>))}</div>
        </div>
        <div onClick={() => setActiveDemo('finance')} className="p-6 rounded-xl bg-white cursor-pointer hover:shadow-lg transition-all" style={{ border: '2px solid #0f4c81', borderTop: '4px solid #0f4c81' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: '#0f4c8115' }}><DollarSign size={24} style={{ color: '#0f4c81' }} /></div>
          <h3 className="text-base font-bold mb-1" style={{ color: C.text }}>Demo 2: CROSSBASE FINANCE</h3>
          <p className="text-sm font-medium mb-2" style={{ color: '#0f4c81' }}>SME Financial Marketplace</p>
          <p className="text-xs leading-relaxed mb-3" style={{ color: C.muted }}>Financial marketplace aggregating all banks and financial services. SMEs browse, compare, and apply for loans, grants, accounts, and insurance.</p>
          <div className="flex flex-wrap gap-1">{['Marketplace','SME Experience','Bank Portal','Compare Tool','Ministry Insights'].map(f => (<span key={f} className="text-[9px] px-2 py-0.5 rounded-full font-medium" style={{ background: '#0f4c8110', color: '#0f4c81' }}>{f}</span>))}</div>
        </div>
      </div>
    </div>
  );
};

const ICON_OPTIONS = ['stethoscope', 'calculator', 'shield', 'leaf', 'car', 'briefcase', 'target', 'cpu', 'award', 'users'];
const COLOR_OPTIONS = ['#059669', '#2563eb', '#dc2626', '#16a34a', '#7c3aed', '#d97706', '#0891b2', '#be185d'];

const AddProjectForm = ({ token, onDone }) => {
  const [form, setForm] = useState({ name: '', subtitle: '', description: '', icon: 'briefcase', color: '#2563eb', launch_date: '', owner: 'voxit', developers: 0, hourly_rate: 0, duration_months: 0, funding_type: '', overhead_percent: 15 });
  const [saving, setSaving] = useState(false);
  const rawCost = form.developers * form.hourly_rate * 165 * form.duration_months;
  const overhead = rawCost * (form.overhead_percent / 100);
  const totalCost = rawCost + overhead;
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api('/projects', token, { method: 'POST', body: JSON.stringify(form) }); toast.success('Project created'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-4" style={{ color: C.text }}>New Project</h3>
      <FormField label="Project Name"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g. HealthConnect" className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Subtitle"><input value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} placeholder="e.g. Health Management Platform" className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Description"><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Brief description..." className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Owner">
        <select value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="voxit">VoxIT</option><option value="ministry">Ministry of SME</option>
        </select>
      </FormField>
      <FormField label="Launch Date"><input type="date" value={form.launch_date} onChange={e => setForm(f => ({ ...f, launch_date: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <div className="p-3 rounded-lg mb-3" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <span className="text-xs font-bold block mb-2" style={{ color: '#0369a1' }}>Project Value (165 hrs/month)</span>
        <div className="grid grid-cols-3 gap-2">
          <FormField label="Developers"><input type="number" min="0" value={form.developers} onChange={e => setForm(f => ({ ...f, developers: Number(e.target.value) }))} className={inputCls} style={inputStyle} /></FormField>
          <FormField label="$/hr rate"><input type="number" min="0" value={form.hourly_rate} onChange={e => setForm(f => ({ ...f, hourly_rate: Number(e.target.value) }))} className={inputCls} style={inputStyle} /></FormField>
          <FormField label="Months"><input type="number" min="0" value={form.duration_months} onChange={e => setForm(f => ({ ...f, duration_months: Number(e.target.value) }))} className={inputCls} style={inputStyle} /></FormField>
        </div>
        {rawCost > 0 && (
          <div className="mt-2 space-y-1">
            <div className="flex justify-between text-xs" style={{ color: C.muted }}><span>Raw ({form.developers} dev x ${form.hourly_rate}/hr x 165hrs x {form.duration_months}mo)</span><span className="font-semibold">${rawCost.toLocaleString()}</span></div>
            <div className="flex justify-between text-xs items-center gap-2" style={{ color: C.muted }}>
              <span className="flex items-center gap-1">Overhead <input type="number" min="0" max="100" value={form.overhead_percent} onChange={e => setForm(f => ({ ...f, overhead_percent: Number(e.target.value) }))} className="w-12 px-1 py-0.5 rounded text-center text-xs" style={{ background: 'white', border: `1px solid ${C.border}` }} />%</span>
              <span className="font-semibold">${overhead.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm font-bold pt-1" style={{ color: '#0369a1', borderTop: '1px solid #bae6fd' }}><span>Total Project Value</span><span>${totalCost.toLocaleString()}</span></div>
          </div>
        )}
        <FormField label="Funding Type">
          <select value={form.funding_type} onChange={e => setForm(f => ({ ...f, funding_type: e.target.value }))} className={inputCls} style={inputStyle}>
            <option value="">Not set</option><option value="grant">Grant</option><option value="paid">Paid</option><option value="partnership">Partnership</option>
          </select>
        </FormField>
      </div>
      <FormField label="Icon">
        <div className="flex flex-wrap gap-2">
          {ICON_OPTIONS.map(ic => { const Ic = getIcon(ic); return (
            <button type="button" key={ic} onClick={() => setForm(f => ({ ...f, icon: ic }))}
              className="w-9 h-9 rounded-lg flex items-center justify-center transition-all" style={{ background: form.icon === ic ? `${form.color}20` : C.bg, border: form.icon === ic ? `2px solid ${form.color}` : `1px solid ${C.border}` }}>
              <Ic size={16} style={{ color: form.icon === ic ? form.color : C.muted }} />
            </button>
          ); })}
        </div>
      </FormField>
      <FormField label="Color">
        <div className="flex flex-wrap gap-2">
          {COLOR_OPTIONS.map(c => (
            <button type="button" key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
              className="w-7 h-7 rounded-full transition-all" style={{ background: c, border: form.color === c ? '3px solid #1e293b' : '2px solid transparent', transform: form.color === c ? 'scale(1.2)' : 'scale(1)' }} />
          ))}
        </div>
      </FormField>
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: form.color }}>
        {saving ? 'Creating...' : 'Create Project'}
      </button>
    </form>
  );
};

const EditProjectForm = ({ item, token, onDone }) => {
  const [form, setForm] = useState({ name: item.name || '', subtitle: item.subtitle || '', description: item.description || '', icon: item.icon || 'briefcase', color: item.color || '#2563eb', launch_date: item.launch_date || '', owner: item.owner || 'voxit', developers: item.developers || 0, hourly_rate: item.hourly_rate || 0, duration_months: item.duration_months || 0, funding_type: item.funding_type || '', overhead_percent: item.overhead_percent ?? 15 });
  const [saving, setSaving] = useState(false);
  const rawCost = form.developers * form.hourly_rate * 165 * form.duration_months;
  const overhead = rawCost * (form.overhead_percent / 100);
  const totalCost = rawCost + overhead;
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api(`/projects/${item.id}`, token, { method: 'PUT', body: JSON.stringify(form) }); toast.success('Project updated'); onDone(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="p-6">
      <h3 className="text-lg font-bold mb-4" style={{ color: C.text }}>Edit Project</h3>
      <FormField label="Project Name"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Subtitle"><input value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Description"><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className={inputCls} style={inputStyle} /></FormField>
      <FormField label="Owner">
        <select value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} className={inputCls} style={inputStyle}>
          <option value="voxit">VoxIT</option><option value="ministry">Ministry of SME</option>
        </select>
      </FormField>
      <FormField label="Launch Date"><input type="date" value={form.launch_date} onChange={e => setForm(f => ({ ...f, launch_date: e.target.value }))} className={inputCls} style={inputStyle} /></FormField>
      <div className="p-3 rounded-lg mb-3" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <span className="text-xs font-bold block mb-2" style={{ color: '#0369a1' }}>Project Value (165 hrs/month)</span>
        <div className="grid grid-cols-3 gap-2">
          <FormField label="Developers"><input type="number" min="0" value={form.developers} onChange={e => setForm(f => ({ ...f, developers: Number(e.target.value) }))} className={inputCls} style={inputStyle} /></FormField>
          <FormField label="$/hr rate"><input type="number" min="0" value={form.hourly_rate} onChange={e => setForm(f => ({ ...f, hourly_rate: Number(e.target.value) }))} className={inputCls} style={inputStyle} /></FormField>
          <FormField label="Months"><input type="number" min="0" value={form.duration_months} onChange={e => setForm(f => ({ ...f, duration_months: Number(e.target.value) }))} className={inputCls} style={inputStyle} /></FormField>
        </div>
        {rawCost > 0 && (
          <div className="mt-2 space-y-1">
            <div className="flex justify-between text-xs" style={{ color: C.muted }}><span>Raw ({form.developers} dev x ${form.hourly_rate}/hr x 165hrs x {form.duration_months}mo)</span><span className="font-semibold">${rawCost.toLocaleString()}</span></div>
            <div className="flex justify-between text-xs items-center gap-2" style={{ color: C.muted }}>
              <span className="flex items-center gap-1">Overhead <input type="number" min="0" max="100" value={form.overhead_percent} onChange={e => setForm(f => ({ ...f, overhead_percent: Number(e.target.value) }))} className="w-12 px-1 py-0.5 rounded text-center text-xs" style={{ background: 'white', border: `1px solid ${C.border}` }} />%</span>
              <span className="font-semibold">${overhead.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm font-bold pt-1" style={{ color: '#0369a1', borderTop: '1px solid #bae6fd' }}><span>Total Project Value</span><span>${totalCost.toLocaleString()}</span></div>
          </div>
        )}
        <FormField label="Funding Type">
          <select value={form.funding_type} onChange={e => setForm(f => ({ ...f, funding_type: e.target.value }))} className={inputCls} style={inputStyle}>
            <option value="">Not set</option><option value="grant">Grant</option><option value="paid">Paid</option><option value="partnership">Partnership</option>
          </select>
        </FormField>
      </div>
      <FormField label="Icon">
        <div className="flex flex-wrap gap-2">
          {ICON_OPTIONS.map(ic => { const Ic = getIcon(ic); return (
            <button type="button" key={ic} onClick={() => setForm(f => ({ ...f, icon: ic }))}
              className="w-9 h-9 rounded-lg flex items-center justify-center transition-all" style={{ background: form.icon === ic ? `${form.color}20` : C.bg, border: form.icon === ic ? `2px solid ${form.color}` : `1px solid ${C.border}` }}>
              <Ic size={16} style={{ color: form.icon === ic ? form.color : C.muted }} />
            </button>
          ); })}
        </div>
      </FormField>
      <FormField label="Color">
        <div className="flex flex-wrap gap-2">
          {COLOR_OPTIONS.map(c => (
            <button type="button" key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
              className="w-7 h-7 rounded-full transition-all" style={{ background: c, border: form.color === c ? '3px solid #1e293b' : '2px solid transparent', transform: form.color === c ? 'scale(1.2)' : 'scale(1)' }} />
          ))}
        </div>
      </FormField>
      <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg font-semibold text-white mt-2 transition-all hover:brightness-110 disabled:opacity-50" style={{ background: form.color }}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
};

const EmptyState = ({ text }) => (
  <div className="text-center py-12"><p className="text-sm" style={{ color: C.light }}>{text}</p></div>
);

export default SMEWorkspacePage;
